import { BILLING_ADDONS, PLAN_NAMES } from 'constants/billing';

const upgradeOrgSavedPromptsTitle = `Please upgrade to an ${PLAN_NAMES.organization} plan or above to create and view org-level saved prompts`;

export const L10nCopilot = {
  askAvoma: 'Ask Avoma',
  askAvomaForDeals: 'Ask Avoma for Deals',
  askAvomaForCompanies: 'Ask Avoma for Companies',
  globalAskAvoma: 'Global Ask Avoma',
  copilotDisabledTooltip:
    'Ask Avoma is not available for this conversation. Please contact support for assistance.',
  copilotDisabledMeetingNotReadyTooltip:
    'A transcript is required to use Ask Avoma for this conversation.',
  copilotDisabledSpeakerDiNotReadyTooltip:
    'Speaker identification must be completed to use Ask Avoma for this conversation. Please contact support for assistance.',
  suggestedPrompts: 'Suggested Prompts',
  showSuggestedPrompts: 'Show Suggested Prompts',
  hideSuggestedPrompts: 'Hide Suggested Prompts',
  copiedToClipboardToast: 'Message copied to clipboard',
  promptCopiedToClipboardToast: 'Prompt copied to clipboard',
  copyMessageTooltip: 'Copy message',
  copyResponseTooltip: 'Copy response',
  copyPromptTooltip: 'Copy prompt',
  addToNotesTooltip: 'Add response to notes',
  addToNotesDisabledTooltip:
    'Add to notes is not available for this conversation',
  resubmitMessageTooltip: 'Resubmit message',
  reconnect: 'Reconnect',
  reconnectTooltip: 'Reconnect to Avoma AI',
  avomaAiMemberName: 'Avoma AI',
  thumbsUpTooltip: 'Helpful',
  thumbsDownTooltip: 'Not helpful',
  feedbackTitle: 'Tell us more',
  additionalFeedbackPlaceholder: 'Provide additional feedback',
  sendFeedbackTooltip: 'Send feedback',
  feedbackDialogTitle: 'Send feedback',
  copilotFeedbackPlaceholder: 'Provide feedback about this conversation',
  tabMode: 'Tab mode',
  multitaskingMode: 'Multitasking mode',
  fullScreenMode: 'Full screen mode',
  switchToTabMode: 'Switch to tab mode',
  multitaskingModeInfo:
    'You are currently using Ask Avoma in multitasking mode',
  moreActions: 'More actions',
  feedbackSuccessToast: 'Feedback sent successfully',
  feedbackErrorToast: 'Failed to send feedback',
  sendMessageError: 'Failed to send message',
  connectionLostErrorMessage:
    'Connection lost. Check your network and try sending your message again.',
  /** Inline error when the latest user message has no Ask Avoma reply yet. */
  copilotNoReplyStartedTimeoutMessage:
    "Ask Avoma couldn't respond. Try sending your message again.",
  /** Inline error when a reply from Ask Avoma did not finish. */
  copilotInProgressStallTimeoutMessage:
    "Ask Avoma couldn't complete that response. Try sending your message again.",
  /** Fallback when the UI shows an inline error but the reason is unknown or new. */
  copilotUnexpectedErrorMessage:
    'Something went wrong. Try sending your message again.',
  upgradeTitle: `Please upgrade to a ${PLAN_NAMES.team} plan or above to access Ask Avoma for meetings.`,
  upgradeMeetingContextCompanyTitle: `Purchase/request a ${BILLING_ADDONS['ri-addon']} add-on to use Ask Avoma across companies.`,
  upgradeMeetingContextDealTitle: `Purchase/request a ${BILLING_ADDONS['ri-addon']} add-on to use Ask Avoma across deals.`,
  upgradeGlobalContextTitle: `Purchase/request a ${BILLING_ADDONS['ci-addon']} add-on to use global Ask Avoma across all conversations`,
  /** TODO: Remove after trial date is over (2026-06-30)
   * Time-bound notice for orgs trialing Global Ask Avoma via the `copilot_global` flag without the licensed feature. */
  globalAskAvomaTrialEndingBanner: (daysRemaining: number) => ({
    prefix: `Your organization’s Global Ask Avoma beta trial ends ${
      daysRemaining <= 0 ? '' : 'in '
    }`,
    countdown:
      daysRemaining <= 0
        ? 'today'
        : `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}`,
    suffix: ` (June 30). Contact your admin to add the ${BILLING_ADDONS['ci-addon']} add-on or upgrade to ${PLAN_NAMES.enterprise} to maintain access. You will be able to continue to use Ask Avoma for individual meetings.`
  }),
  /**
   * Global-shell gate (no meeting/deal/company in the URL): explains why the composer is blocked
   * instead of only showing the generic global add-on line.
   */
  upgradeGlobalAskAvomaNonEntityShellMessage: `Global Ask Avoma isn’t available on your plan for this page. To use it, open a specific meeting, deal, or company—or upgrade with the ${BILLING_ADDONS['ci-addon']} add-on.`,
  /** Shown when the user already has one meeting in scope but Global Ask Avoma is not licensed (single-meeting limit). */
  singleMeetingContextGlobalLicenseNotice: `Your plan includes Ask Avoma for individual meetings. Purchase/request a ${BILLING_ADDONS['ci-addon']} add-on to add multiple meetings as context in the same Ask Avoma conversation.`,
  addToNotesSearchPlaceholder: 'Search for a category...',
  messageAddedToNotes: 'Added response to notes successfully',
  meetingCitationTooltip: 'View meeting in a new tab',
  emailCitationTooltip: 'View email in a new tab',
  timestampCitationTooltip: 'View transcript in a new tab',
  /** Inline timestamp in citation popover when the cited meeting is the meeting/transcript shell (in-tab seek). */
  timestampCitationTooltipSameShellMeeting:
    'Jump to this moment in the meeting',
  /** Timestamp citation hover when transcript text cannot be loaded off the meeting page. */
  citationTranscriptUnavailableOpenInMeetingAskAvoma:
    'Use the link below to view this citation in context on the meeting detail page and see where it appears in the discussion.',
  citationOpenMeetingAskAvomaLink: 'View chat in meeting',
  speakerAndTranscriptNotAvailable: 'Speaker and transcript not available',
  transcriptNotAvailable: 'Transcript not available',
  contextDropdownTitleLong: 'Ask Avoma about',
  contextDropdownTitleShort: 'Ask about',
  contextMentionMeetings: 'Meetings',
  /** Composer @-mention context picker left rail (empty query); distinct from Add context tab labels. */
  contextMentionMyMeetings: 'My meetings',
  /** Meetings right pane when Ask Avoma is scoped to the open meeting. */
  contextMentionThisMeetingSectionHeader: 'This meeting',
  /** Add context / @-mention: scoped deal or company row (parallel to “This meeting”). */
  contextMentionThisDeal: 'This deal',
  contextMentionThisCompany: 'This company',
  /** Add context: rows already added to the message, listed first for uncheck. */
  contextMentionSelectedContextSection: 'Selected context',
  /**
   * Composer control beside Add context: primary meeting / deal / company scope (row-group
   * shortcuts). Shown as panel header and trigger label (same copy as `contextDropdownTitleLong`).
   */
  contextMentionLeftRailSwitchContextsHeading: 'Ask Avoma about…',
  /** Scope mini-headers inside the “Ask Avoma about” switch popover (meetings / deals / companies rows). */
  contextMentionLeftRailSwitchPopoverDeals: 'Associated deals',
  contextMentionLeftRailSwitchPopoverCompanies: 'Associated companies',
  /**
   * Suffix for meeting CRM section headers; combined with pluralized “Deal(s)” / “Company(ies)”.
   * e.g. “Deal associated with this meeting”, “Deals associated with this meeting”.
   */
  contextMentionCrmEntitiesAssociatedWithThisMeeting:
    'associated with this meeting',
  contextMentionDeals: 'Deals',
  contextMentionMyDeals: 'My deals',
  contextMentionCompanies: 'Companies',
  contextMentionMyCompanies: 'My companies',
  /**
   * Pre-selected Add context pill when CRM name is still unknown — never use “This deal” / “This company”.
   */
  contextPillUnnamedDeal: 'Deal',
  contextPillUnnamedCompany: 'Company',
  contextPillUnnamedMeeting: 'Meeting',
  contextPillUnnamedPlaylist: 'Playlist',
  contextPillUnnamedTeam: 'Team',
  /** User-message context pill hover card → SPA navigation */
  standaloneContextPillOpenMeeting: 'Open meeting',
  standaloneContextPillOpenDeal: 'Open deal',
  standaloneContextPillOpenCompany: 'Open company',
  standaloneContextPillOpenPlaylist: 'Open playlist',
  contextMentionSearchPlaceholderMeetings: 'Search for a meeting…',
  contextMentionSearchPlaceholderDeals: 'Search for a deal…',
  contextMentionSearchPlaceholderCompanies: 'Search for a company…',
  contextMentionSearchPlaceholderTeams: 'Search for a team…',
  contextMentionSearchPlaceholderMembers: 'Search for a member…',
  contextMentionSearchPlaceholderPlaylists: 'Search for a playlist…',
  contextMentionNoMatches: 'No matching results found',
  /** Composer @ browse (empty query): active category has no default rows to show. */
  contextMentionNoMeetingsFound: 'No meetings found',
  contextMentionNoDealsFound: 'No deals found',
  contextMentionNoCompaniesFound: 'No companies found',
  contextMentionNoTeamsFound: 'No teams found',
  contextMentionNoMembersFound: 'No members found',
  contextMentionNoPlaylistsFound: 'No playlists found',
  contextMentionTypeToSearchHint: 'Start typing to search across all contexts',
  contextMentionOmnisearchLoading: 'Searching…',
  contextMentionDefaultOmnisearchLoading: 'Loading…',
  contextMentionMembers: 'Members',
  /** Secondary line for meeting-derived member rows when the participant is external to the signed-in org’s bucket. */
  contextMentionParticipantExternal: 'External',
  contextMentionMembersAssociatedWithThisMeeting:
    'People associated with this meeting',
  contextMentionMembersAssociatedWithThisCall:
    'People associated with this call',
  contextMentionMembersAssociatedWithThisDeal:
    'People associated with this deal',
  contextMentionMembersAssociatedWithThisCompany:
    'People associated with this company',
  contextMentionTeams: 'Teams',
  contextMentionMyTeams: 'My teams',
  /** Team context row subtitle: combined with leading count in code, e.g. “2 members”. */
  contextMentionTeamMemberWordSingular: 'member',
  contextMentionTeamMemberWordPlural: 'members',
  contextMentionPlaylists: 'Playlists',
  contextMentionMyPlaylists: 'My playlists',
  addContextRefineContext: 'Refine context',
  addContextButtonLabel: 'Add context',
  /** User bubble: standalone context pills without a known categoryKey (grouped menu label). */
  standaloneContextPillsOtherCategoryLabel: 'Context',
  contextMentionClearAll: 'Clear all',
  contextMentionRemoveFromContext: 'Remove from context',
  associatedCompany: 'Associated company',
  associatedCompanies: 'Associated companies',
  associatedDeal: 'Associated deal',
  associatedDeals: 'Associated deals',
  savedPrompts: 'Prompt library',
  webSearchLabel: 'Web search',
  webSearchTooltip:
    'When enabled, Avoma can search the web to answer your message',
  webSearchDismissAria: 'Turn off web search',
  savePrompt: 'Save prompt',
  saveScheduledPrompt: 'Save as scheduled prompt',
  duplicatePromptTooltip: 'This prompt has already been saved',
  noSavedPrompts: 'No saved prompts',
  untitledPrompt: 'Untitled prompt',
  /** Label used in composer `@[display](id)` when the mention display sanitizes to an empty string. */
  copilotContextMentionUnknownDisplay: 'Unknown',
  deletePromptTooltip: 'Delete prompt',
  usePromptTooltip: 'Use prompt',
  editPromptTooltip: 'Edit prompt',
  editScheduledPromptTooltip: 'Edit scheduled prompt',
  createScheduleTooltip: 'Create schedule',
  editScheduleTooltip: 'Edit schedule',
  /** History sidebar / scheduled report actions (removes the schedule and its reports). */
  deleteScheduleTooltip: 'Remove schedule',
  savedPromptSuccessToast: 'Saved prompt successfully',
  savedPromptErrorToast: 'Failed to save prompt',
  savedPromptDeleteSuccessToast: 'Deleted prompt successfully',
  savedPromptDeleteErrorToast: 'Failed to delete prompt',
  searchSavedPromptsPlaceholder: 'Search for a saved prompt...',
  /** Unified Prompt Library search: prompt titles and body only (not collections). */
  searchSavedPromptsPlaceholderUnified: 'Search for a prompt...',
  searchSavedPromptsPlaceholderPrivate: 'Search for your prompt...',
  searchSavedPromptsPlaceholderOrganization: 'Search for an org prompt...',
  searchSavedPromptsPlaceholderDefault: 'Search for a default prompt...',
  searchSavedPromptsPlaceholderCollections: 'Search for a collection...',
  searchSavedPromptsPlaceholderScheduledForYou:
    'Search for a scheduled prompt...',
  noSavedPromptsFound: 'No matching prompts found',
  noCollectionsFound: 'No collections',
  noMatchingCollectionsFound: 'No matching collections found',
  searchResultsTitle: 'Search results',
  clearSearchResults: 'Clear search',
  searchResultsGroupMyPrompts: 'My prompts',
  searchResultsGroupTeammates: "Teammates' prompts",
  searchResultsGroupDefault: 'Default prompts',
  savedPromptsEmpty: 'No prompts',
  collectionPrivatePromptsInfo:
    'Collection may contain private prompts not visible to you',
  noMembersFound: 'No members found',
  noSortOptionsFound: 'No sort options found',
  sortBy: 'Sort by',
  filterByVisibility: 'Filter by privacy',
  visibilityFilterAll: 'All',
  visibilityFilterPrivate: 'Only me',
  visibilityFilterOrg: 'Org-level',
  askAvomaPlaceholder:
    'Ask Avoma about anything. Use @ to refer to specific meetings, deals, companies, playlists, teams, and members.',
  /** Composer when @ context mentions are disabled (e.g. single-meeting CI, deal without global). */
  askAvomaPlaceholderWithoutContextMentions: 'Ask Avoma about...',
  askAvomaSettingsTitle: 'Customize Ask Avoma',
  responsePreferencesLabel: 'How should Ask Avoma respond?',
  responsePreferencesHelperText:
    'Set a preferred tone, level of detail, and format for all responses',
  responsePreferencesPlaceholder:
    'e.g. use a concise, executive-friendly tone with bullet points and clear next steps',
  customizeResponseSuccessToast: 'Response customization updated successfully',
  customizeResponseErrorToast: 'Failed to update response customization',
  askAvomaDisclaimer: 'Ask Avoma is AI and can make mistakes.',
  includeTimestampsOnCopy: 'Include meeting timestamps when copying responses?',
  viewAllMessages: 'View all messages',
  showLess: 'Hide older messages',
  newChat: 'New chat about...',
  /** Single-row CTA when the page has one clear context target (not the short `newChat` dropdown label). */
  newChatAboutPrefix: 'New chat about',
  /** History sidebar: create a new session with global (non-URL) context from a page that also offers meeting/deal/company. */
  newChatMenuGlobal: 'Anything',
  openHistory: 'Open history',
  closeHistory: 'Close history',
  searchHistoryPlaceholder: 'Search chats...',
  noChatHistoryFound: 'No chat history',
  noSearchResultsFound: 'No matching chats found',
  noScheduledPromptsFound: 'No scheduled prompts found',
  noSearchResultsFoundScheduledPrompts: 'No matching schedules found',
  deleteChatTitle: 'Delete chat?',
  deleteScheduledPromptTitle: 'Delete scheduled prompt?',
  /** Report list / history / manage: removing automation only (PATCH), not DELETE prompt. */
  removeScheduledPromptScheduleTitle: 'Remove schedule?',
  removeScheduledPromptScheduleLead:
    'Are you sure you want to remove the schedule for',
  removeScheduledPromptScheduleTrailing:
    '? Automated runs will stop. Past reports stay available, and the saved prompt is not deleted.',
  removeScheduledPromptScheduleConfirm: 'Remove schedule',
  removeScheduledPromptScheduleSuccessToast: 'Schedule removed',
  deleteOrgPromptTitle: 'Delete organization prompt?',
  deleteOrgPromptButton: 'Delete prompt',
  deleteOrgPromptAffectedMembers:
    'Schedules will be deleted for the following members:',
  deleteReportTooltip: 'Delete report',
  deleteReportTitle: 'Delete report?',
  deleteChatSuccessToast: 'Chat deleted successfully',
  deleteChatErrorToast: 'Failed to delete chat',
  updateChatNameSuccessToast: 'Chat renamed successfully',
  updateChatNameErrorToast: 'Failed to rename chat',
  scrollToBottom: 'Scroll to bottom',
  scrollToTop: 'Scroll to top',
  scrollToNextMessage: 'Scroll to next message',
  scrollToPreviousMessage: 'Scroll to previous message',
  isPlayingDisabledTooltip: 'Pause the video to view the citation',
  pauseScheduledPromptSuccessToast: 'Scheduled prompt paused',
  schedulePausedBadgeLabel: 'Schedule paused',
  resumeScheduledPromptSuccessToast: 'Scheduled prompt resumed',
  markAllScheduledPromptReportsAsReadSuccessToast:
    'All scheduled prompt reports marked as read',
  updateScheduledPromptSuccessToast: 'Scheduled prompt updated successfully',
  updateScheduledPromptErrorToast: 'Failed to update scheduled prompt',
  deleteScheduledPromptSuccessToast: 'Scheduled prompt deleted successfully',
  deleteScheduledPromptErrorToast: 'Failed to delete scheduled prompt',
  createScheduledPromptSuccessToast: 'Scheduled prompt created successfully',
  createScheduledPromptErrorToast: 'Failed to create scheduled prompt',
  deletePromptAndSchedule: 'Delete prompt & schedule',
  generatePromptNameSuccessToast: 'Name generated successfully',
  generatePromptNameErrorToast: 'Failed to generate prompt name',
  refinePromptSuccessToast: 'Prompt refined successfully',
  refinePromptErrorToast: 'Failed to refine prompt',
  new: 'New',
  newPrompt: 'New prompt',
  newScheduledPrompt: 'Schedule a new prompt',
  newScheduledPromptDisabledTooltip:
    "You've reached the limit of 10 active scheduled prompts. Pause or delete an existing schedule to create another.",
  updateScheduledPromptFrequencyDisabledTooltip:
    "You've reached the limit of 10 active scheduled prompts. Pause or delete an existing schedule to enable another.",
  resumeScheduledPromptDisabledTooltip:
    "You've reached the limit of 10 active scheduled prompts. Pause or delete an existing prompt to resume the schedule for this prompt.",
  manageScheduledPrompts: 'Manage scheduled prompts',
  scheduled: 'Scheduled',
  chats: 'Chats',
  backToScheduledPrompt: 'Back to scheduled prompt',
  updatePromptInfo:
    'Any changes made to this prompt will take effect on its next scheduled run.',
  updateScheduleInfo:
    'Any changes made to this frequency will take effect on its next scheduled run.',
  scheduledPromptPausedInfo:
    'This schedule is currently paused. Your frequency and notification settings are preserved and will be honored when you resume the schedule.',
  scheduledPromptNamePlaceholder: 'e.g. Engagement analysis',
  schedulePrompt: 'Schedule prompt',
  newScheduledPromptInfo:
    'Save time by scheduling prompts to run automatically and surface insights at a regular frequency.',
  scheduledUserInfo:
    'This prompt will be scheduled for you only, not your organization',
  editOrgPromptInfo:
    'You are currently editing an organization-wide prompt. All scheduled uses will reflect any updates to this prompt, but existing schedules will remain unchanged.',
  downgradingOrgPromptToPrivateInfo:
    'If you save this prompt as private, your organization will no longer have access, and any schedules for other members will be removed.',
  createOrgPromptWarning:
    "After saving this as an organization prompt, it can't be made private later.",
  downgradeOrgPromptToPrivateTitle: 'Make this prompt private?',
  downgradeOrgPromptToPrivateMessage:
    "Saving this prompt as private will remove organization access and delete all schedules for other members. The prompt author's schedule will remain unaffected.",
  downgradeOrgPromptToPrivateAffectedMembers:
    'Schedules will be deleted for the following members:',
  saveAsPrivate: 'Save as private',
  discardChangesTitle: 'Discard unsaved changes?',
  discardChangesMessage:
    'You have unsaved changes. If you proceed without saving, your changes will be lost and you will not be able to recover them.',
  continueEditing: 'Continue editing',
  discardChanges: 'Discard changes',
  saveChanges: 'Save changes',
  create: 'Create',
  cancel: 'Cancel',
  orgSavedPrompts: 'Org Saved Prompts',
  upgradeOrgSavedPromptsTitle,
  promptsMayPause:
    'Scheduled prompts may pause when credits are exhausted. Contact your admin to purchase additional credits.',
  allScheduledPromptsPaused: 'All scheduled prompts are currently paused',
  promptLibraryGateTitle: 'Prompt Library requires a paid license',
  promptLibraryGateSubtitles: [
    'Create and manage custom AI prompts for Ask Avoma',
    'Save frequently used prompts for quick access',
    'Share prompts across your organization'
  ],
  promptLibraryBreadcrumbText: 'Prompt Library',
  newCollection: 'New collection',
  defaultPromptCollectionLocked:
    'Default prompts cannot have their collections changed',
  addToCollectionHelperDescription:
    'Organize your prompts by adding them to a collection, including default collections, for easier access. You can remove a prompt from a collection the same way.',
  scheduledForYou: 'Scheduled for you',
  promptCollectionVisibilityInfo:
    'Collections are visible to everyone in your organization and can include both private and organization-level prompts. Any private prompts included in a collection remain visible only to the user who created them.',
  collectionName: 'Name',
  collectionNamePlaceholder: 'e.g. Onboarding Collection',
  collectionDescription: 'Description',
  collectionDescriptionPlaceholder: 'e.g. Collection for onboarding process',
  editCollection: 'Edit collection',
  createNewCollection: 'Create a new collection',
  collectionCreatedSuccessToast: 'Collection created successfully',
  collectionCreatedErrorToast: 'Failed to create collection. Please try again.',
  collectionUpdatedSuccessToast: 'Collection updated successfully',
  collectionUpdatedErrorToast: 'Failed to update collection. Please try again.',
  collectionDeletedSuccessToast: 'Collection deleted successfully',
  collectionDeletedErrorToast: 'Failed to delete collection. Please try again.',
  creditBalance: 'Credit balance',
  creditBalanceSubtitle: 'Only for scheduled prompts',
  creditsRemainingMessage: 'left for your organization',
  creditsResets: 'Resets on',
  scheduledPromptCreditBannerCriticalTitle: 'Low credits warning.',
  scheduledPromptCreditBannerCriticalPrefix: 'Only',
  scheduledPromptCreditBannerCriticalSuffix:
    'left. This prompt will pause soon. Contact your admin to buy more credits.',
  scheduledPromptCreditBannerDangerTitle: 'Credits exhausted.',
  scheduledPromptCreditBannerDangerResumesOn: 'This Prompt resumes on',
  scheduledPromptCreditBannerDangerSuffix:
    '. Contact your admin to purchase more credits and resume it sooner.',
  scheduledPromptCreditBannerDangerNoDate:
    'Credits exhausted. This prompt will resume when monthly credits reset. Contact your admin to purchase more credits and resume it sooner.',
  scheduledPromptPausedCreditsExhausted: 'Paused・Credits exhausted',
  scheduledPromptPausedCreditsExhaustedMessage:
    'This prompt was paused because your organization has exhausted its credits. Credits reset monthly.',
  scheduledPromptPausedCreditsExhaustedContact:
    'Contact your admin to buy credits'
} as const;
